import { Fragment, useMemo } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Tooltip } from '@odo/shared-ui';
import type { JourneySegment } from '../../../services/timeline';
import { useStatusCatalog, type StatusCatalog } from '../../../services/statusCatalog';
import { formatDurationMs, formatShortDate } from './event-time';
import { PHASE_HEX, phaseTint } from '../../../utils/phaseColors';
import { TIER_TEXT_CLASS, resolveLatenessTierFromMs } from '../../work-queue/timeThresholds';

interface JourneyViewProps {
    /** Segments as returned by the API (oldest first). */
    segments: JourneySegment[];
    isLoading: boolean;
    isError: boolean;
}

interface PhaseGroup {
    key: string;
    phase: string | null;
    totalMs: number | null;
    ongoing: boolean;
    /** Newest first within the group. */
    segments: JourneySegment[];
}

// Groups consecutive same-phase segments (ascending input), then flips both
// the groups and the segments inside each so the view reads newest-first,
// matching the Activity feed.
function groupByPhase(ascending: JourneySegment[]): PhaseGroup[] {
    const groups: PhaseGroup[] = [];
    for (const segment of ascending) {
        const last = groups[groups.length - 1];
        if (last && last.phase === segment.phase) {
            last.segments.push(segment);
            last.totalMs =
                segment.durationMs != null
                    ? (last.totalMs ?? 0) + segment.durationMs
                    : last.totalMs;
            last.ongoing = last.ongoing || segment.leftAt == null;
        } else {
            groups.push({
                key: `${segment.phase ?? 'unknown'}-${groups.length}`,
                phase: segment.phase,
                totalMs: segment.durationMs,
                ongoing: segment.leftAt == null,
                segments: [segment]
            });
        }
    }
    return groups.map((g) => ({ ...g, segments: [...g.segments].reverse() })).reverse();
}

function dateRange(segment: JourneySegment): string {
    const start = segment.enteredAt ? formatShortDate(segment.enteredAt) : '?';
    const end = segment.leftAt ? formatShortDate(segment.leftAt) : 'now';
    return `${start} → ${end}`;
}

// Durations reuse the work queue's lateness ladders: per-step ('step'
// thresholds) for stations, per-phase for group totals. A tier class wins
// over the ongoing/neutral styling — a late duration should read as late
// whether or not the stay is finished.
function stationDurationClass(segment: JourneySegment, ongoing: boolean): string {
    const tier =
        segment.durationMs != null
            ? resolveLatenessTierFromMs(segment.durationMs, 'step', segment.status)
            : null;
    if (tier) return TIER_TEXT_CLASS[tier];
    return ongoing ? 'text-emerald-600 font-medium' : 'text-gray-400';
}

function phaseTotalClass(group: PhaseGroup): string {
    const newest = group.segments[0];
    const tier =
        group.totalMs != null && newest
            ? resolveLatenessTierFromMs(group.totalMs, 'phase', newest.status)
            : null;
    return tier ? TIER_TEXT_CLASS[tier] : 'text-gray-500';
}

// The rail geometry mirrors TimelineRailEvent: rows are `relative pl-7`, the
// connector line sits at left-[10px], and nodes are centered on it. Coloring
// the line and nodes per phase turns the same skeleton into a route map.
const RAIL_LEFT_CLASS = 'left-[10px]';

/** Phase marker: a lettered badge on the rail opening a full-width tinted
 *  band — the phase boundary reads as a zone header, not just another stop. */
function PhaseMarkerRow({
    group,
    color,
    isFirst
}: {
    group: PhaseGroup;
    color: string;
    isFirst: boolean;
}) {
    return (
        <li className={`relative pl-7 pb-2 ${isFirst ? '' : 'pt-2.5'}`}>
            <span
                className={`absolute ${RAIL_LEFT_CLASS} top-6 bottom-0 w-[2px]`}
                style={{ background: color }}
            />
            <span
                className={`absolute left-[4px] ${isFirst ? 'top-1' : 'top-3.5'} w-3.5 h-3.5 rounded-full z-10 ring-2 ring-white`}
                style={{ background: color }}
            />
            <div
                className="flex items-baseline gap-1.5 rounded-md px-2 py-1"
                style={{ background: phaseTint(color, 0.06) }}
            >
                <span
                    className="text-[11px] font-bold uppercase tracking-[0.05em] truncate"
                    style={{ color }}
                >
                    {group.phase ?? 'Unknown phase'}
                </span>
                <span className={`text-[10px] whitespace-nowrap ml-auto ${phaseTotalClass(group)}`}>
                    {group.totalMs != null ? formatDurationMs(group.totalMs) : '—'}
                </span>
            </div>
        </li>
    );
}

/** Station: one stay in a status, as a stop on the route. */
function StationRow({
    segment,
    color,
    isLast,
    isLastInGroup,
    catalog
}: {
    segment: JourneySegment;
    color: string;
    isLast: boolean;
    isLastInGroup: boolean;
    catalog: StatusCatalog;
}) {
    const ongoing = segment.leftAt == null;
    return (
        <li className="relative pl-7 pb-2.5">
            {/* Within a phase the route line is solid in the phase color; crossing
          into the next phase it breaks into a dashed gray bridge, so each
          boundary is visible even before the next zone header. */}
            {!isLast &&
                (isLastInGroup ? (
                    <span
                        className={`absolute ${RAIL_LEFT_CLASS} top-3.5 bottom-0 w-0 border-l-2 border-dashed border-gray-300`}
                    />
                ) : (
                    <span
                        className={`absolute ${RAIL_LEFT_CLASS} top-3.5 bottom-0 w-[2px]`}
                        style={{ background: color }}
                    />
                ))}
            <span
                className="absolute left-[5px] top-1 w-3 h-3 rounded-full border-2 bg-white flex items-center justify-center"
                style={{ borderColor: color }}
            >
                {ongoing && (
                    <span
                        className="w-1.5 h-1.5 rounded-full animate-pulse"
                        style={{ background: color }}
                    />
                )}
            </span>
            <div className="leading-tight">
                <div className="flex items-baseline gap-1.5">
                    <span
                        className={`text-xs truncate ${ongoing ? 'font-semibold text-gray-900' : 'text-gray-600'}`}
                    >
                        {catalog.display(segment.status)}
                    </span>
                    {segment.viaAction === 'force-status' && (
                        <Tooltip content="Status forced" side="top">
                            <span className="flex-shrink-0 self-center">
                                <ExclamationTriangleIcon
                                    className="w-3 h-3 text-amber-500"
                                    aria-label="Status forced"
                                />
                            </span>
                        </Tooltip>
                    )}
                    <span
                        className={`text-[10px] whitespace-nowrap ml-auto ${stationDurationClass(segment, ongoing)}`}
                    >
                        {segment.durationMs != null ? formatDurationMs(segment.durationMs) : '—'}
                    </span>
                </div>
                <div className="text-[10px] text-gray-400">{dateRange(segment)}</div>
            </div>
        </li>
    );
}

/**
 * The "Journey" timeline mode: which steps/phases the patient went through
 * and how long each took, drawn as a continuous route on the same vertical
 * rail the Activity feed uses. Newest on top; each phase is a colored line
 * section opened by a lettered phase marker with the phase's total time.
 */
export function JourneyView({ segments, isLoading, isError }: JourneyViewProps) {
    const groups = useMemo(() => groupByPhase(segments), [segments]);
    const catalog = useStatusCatalog();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-6">
                <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-6">
                <div className="text-xs text-red-500 font-inter">Failed to load journey</div>
            </div>
        );
    }

    if (groups.length === 0) {
        return (
            <div className="text-center py-6">
                <div className="text-xs text-gray-400 font-inter">No workflow history yet</div>
            </div>
        );
    }

    return (
        <ul className="relative">
            {groups.map((group, groupIndex) => {
                const color = (group.phase && PHASE_HEX[group.phase]) || 'rgb(156,163,175)';
                const isLastGroup = groupIndex === groups.length - 1;
                return (
                    <Fragment key={group.key}>
                        <PhaseMarkerRow group={group} color={color} isFirst={groupIndex === 0} />
                        {group.segments.map((segment, segmentIndex) => (
                            <StationRow
                                key={`${segment.status}-${segment.enteredAt ?? 'start'}`}
                                segment={segment}
                                color={color}
                                isLast={isLastGroup && segmentIndex === group.segments.length - 1}
                                isLastInGroup={segmentIndex === group.segments.length - 1}
                                catalog={catalog}
                            />
                        ))}
                    </Fragment>
                );
            })}
        </ul>
    );
}
